import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { registerPasskey } from "../../lib/auth.js";

const SUPPORTS_PASSKEY = typeof window !== "undefined" && !!window.PublicKeyCredential;

export default function PortalPasskeys() {
  const [passkeys, setPasskeys] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nickname, setNickname] = useState("");
  const [success, setSuccess] = useState(null);

  async function loadPasskeys() {
    try {
      const res = await fetch("/api/auth/passkey/list", { credentials: "include" });
      if (res.ok) {
        const json = await res.json();
        setPasskeys(json.passkeys || []);
      } else {
        setPasskeys([]);
      }
    } catch { setPasskeys([]); }
  }

  useEffect(() => { loadPasskeys(); }, []);

  async function onAdd() {
    setError(null); setSuccess(null); setBusy(true);
    const res = await registerPasskey(nickname || guessDeviceName());
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    setSuccess("Passkey registered. You can now sign in with it.");
    setNickname("");
    loadPasskeys();
  }

  return (
    <div className="portal-page">
      <h1 className="portal-h1">Passkeys</h1>
      <p className="muted">
        Passkeys let you sign in to your portal using Face ID, Touch ID, Windows Hello,
        or a hardware security key (YubiKey, Titan). They&rsquo;re cryptographically bound
        to <code>invest.daksfirst.com</code> &mdash; phishing-resistant by design.
      </p>

      <section className="portal-card">
        <h2 className="portal-h2">Register a new passkey</h2>
        {!SUPPORTS_PASSKEY && (
          <p className="muted small" style={{ color: "var(--portal-neg)" }}>
            This browser doesn&rsquo;t support passkeys. Use Chrome, Safari, Edge, or Firefox.
          </p>
        )}
        <div className="kyc-field">
          <label>Device label (optional)</label>
          <input className="portal-input"
                 placeholder="e.g. MacBook Pro · iPhone 16 · YubiKey 5"
                 value={nickname}
                 onChange={e => setNickname(e.target.value)} />
        </div>
        <div className="hero-actions spacer-s">
          <button className="btn btn-primary" disabled={!SUPPORTS_PASSKEY || busy} onClick={onAdd}>
            {busy ? "Registering…" : "Register this device →"}
          </button>
        </div>
        {error && <p className="login-error spacer-s">{error}</p>}
        {success && <p className="muted spacer-s" style={{ color: "var(--portal-pos)" }}>{success}</p>}
      </section>

      <section className="portal-card">
        <h2 className="portal-h2">Registered passkeys</h2>
        {!passkeys.length && <p className="muted">No passkeys registered yet.</p>}
        {passkeys.length > 0 && (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr><th>Device</th><th>Type</th><th>Registered</th><th>Last used</th><th></th></tr>
              </thead>
              <tbody>
                {passkeys.map(p => (
                  <tr key={p.id}>
                    <td>{p.nickname || "Untitled"}</td>
                    <td className="muted">{p.deviceType || "—"}</td>
                    <td className="muted">{p.createdAt?.slice(0, 10) || "—"}</td>
                    <td className="muted">{p.lastUsedAt?.slice(0, 10) || "—"}</td>
                    <td><a className="portal-rowlink" href="#" onClick={(e) => { e.preventDefault(); revokePasskey(p.id, loadPasskeys); }}>Revoke</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="portal-card">
        <h2 className="portal-h2">Why passkeys</h2>
        <ul className="deal-bullets">
          <li><b>Phishing-resistant.</b> The passkey only works on invest.daksfirst.com — even a perfect-clone phishing site can&rsquo;t use it.</li>
          <li><b>No password to lose.</b> Your private key never leaves your device or syncs only via your platform&rsquo;s passkey provider.</li>
          <li><b>Multiple devices supported.</b> Register each device you sign in from. Lose a device? Revoke its passkey here.</li>
          <li><b>Hardware key support.</b> YubiKey 5, Titan, or other FIDO2 keys work alongside biometrics.</li>
        </ul>
        <p className="muted small spacer-m">
          Need to sign in without your passkey? Use the &ldquo;email me a sign-in link&rdquo; option on
          the <Link className="portal-link" to="/login">login page</Link>.
        </p>
      </section>
    </div>
  );
}

async function revokePasskey(id, reload) {
  if (!confirm("Revoke this passkey? You won't be able to sign in with it again.")) return;
  await fetch(`/api/auth/passkey/${id}`, { method: "DELETE", credentials: "include" }).catch(() => {});
  reload();
}

function guessDeviceName() {
  const ua = navigator.userAgent || "";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/iPhone/.test(ua))    return "iPhone";
  if (/iPad/.test(ua))      return "iPad";
  if (/Android/.test(ua))   return "Android";
  if (/Windows/.test(ua))   return "Windows";
  return "Device";
}
